// ==========================================================================
// screens/LogTrip.tsx — the two-minute logbook (FF-008, 9 Oct 2026)
// Start km comes from the last trip. End km gets the number pad. Distance is
// worked out live. Purpose is one tap for the usual reasons. Work/private is
// a proposal: it still gets reviewed on the vehicle screen.
// ==========================================================================
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { PageHeader } from "@/components/Page";
import { Button, Card, ErrorText } from "@/components/ui";
import { KmField, NumberPad, applyKey } from "@/components/NumberPad";
import { useEntities } from "@/lib/household";
import { todayIso, useOdometer, useSaveTrip, useTrips, useUsePeriods, useVehicles } from "@/lib/vehicles";

const PURPOSES = ["Site visit", "Depot", "Client meeting", "Supplier / pickup", "Training", "Personal"];

export default function LogTrip() {
  const { id } = useParams();
  const nav = useNavigate();
  const vehicles = useVehicles(); const trips = useTrips(id); const periods = useUsePeriods(); const entities = useEntities(); const odo = useOdometer(id);
  const save = useSaveTrip();
  const vehicle = vehicles.data?.find((v) => v.asset_id === id);
  const lastEnd = useMemo(() => {
    const all = [...(trips.data ?? []).map((t) => t.end_km), ...(odo.data ?? []).map((o) => o.km)];
    return all.length ? Math.max(...all) : null;
  }, [trips.data, odo.data]);
  const workUse = periods.data?.some((p) => p.asset_id === id && p.use === "work" && !p.valid_to) ?? false;
  const employers = entities.data?.filter((e) => e.kind === "company") ?? [];

  const [date, setDate] = useState(todayIso());
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [focus, setFocus] = useState<"start" | "end">("end");
  const [purpose, setPurpose] = useState("");
  const [jobRef, setJobRef] = useState("");
  const [to, setTo] = useState("");
  const [use, setUse] = useState<"work" | "private">(workUse ? "work" : "private");
  const [employer, setEmployer] = useState<string>("");

  useEffect(() => { if (lastEnd !== null && start === "") setStart(String(lastEnd)); }, [lastEnd]); // eslint-disable-line
  useEffect(() => { if (employers.length === 1 && !employer) setEmployer(employers[0].id); }, [employers.length]); // eslint-disable-line
  useEffect(() => { setUse(workUse ? "work" : "private"); }, [workUse]);

  const s = Number(start || 0), e = Number(end || 0);
  const distance = end && start ? e - s : null;
  const bad = distance !== null && distance < 0;
  const far = distance !== null && distance > 1500;

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    if (!id || distance === null || bad) return;
    await save.mutateAsync({ draft: {
      vehicle_id: id, trip_date: date, start_km: s, end_km: e, purpose: purpose.trim(),
      to_place: to.trim() || null, job_ref: jobRef.trim() || null,
      employer_entity_id: use === "work" ? employer || null : null, proposed_use: use,
    } });
    nav(`/vehicles/${id}`);
  }

  return (
    <>
      <PageHeader title={`Log a trip${vehicle ? ` · ${vehicle.assets.name}` : ""}`} subtitle={format(new Date(date + "T00:00"), "EEEE d MMMM")} />
      <form onSubmit={submit} className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-5 lg:col-span-3">
          <Card className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div onFocus={() => setFocus("start")}><KmField label="Start" value={start} onChange={setStart} hint={lastEnd !== null ? "Last known reading — fix it if the dash says otherwise" : "Odometer before you leave"} /></div>
              <div onFocus={() => setFocus("end")}><KmField label="End" value={end} onChange={setEnd} autoFocus hint="Odometer when you arrive" /></div>
            </div>
            <div className="flex items-baseline justify-between rounded-md bg-surface-raised px-4 py-3">
              <span className="text-sm text-ink-muted">Distance</span>
              <span className={`num text-4xl font-semibold ${bad ? "text-bad" : "text-ink"}`}>{distance === null ? "—" : distance.toLocaleString("en-AU")}<span className="ml-1 text-base font-normal text-ink-muted">km</span></span>
            </div>
            {bad && <ErrorText>End reading is lower than the start.</ErrorText>}
            {far && !bad && <p className="text-sm text-warn">That's a long one. Check the readings.</p>}
            <NumberPad onKey={(k) => (focus === "start" ? setStart((v) => applyKey(v, k)) : setEnd((v) => applyKey(v, k)))} />
          </Card>
        </div>

        <div className="space-y-5 lg:col-span-2">
          <Card className="space-y-4">
            <label className="block space-y-1">
              <span className="text-sm font-medium text-ink-muted">Date</span>
              <input type="date" value={date} max={todayIso()} onChange={(ev) => setDate(ev.target.value)} className="w-full rounded-md border border-line bg-bg px-3 py-2 text-ink" />
            </label>
            <div className="space-y-1">
              <span className="text-sm font-medium text-ink-muted">Purpose</span>
              <div className="flex flex-wrap gap-2">
                {PURPOSES.map((p) => (
                  <button key={p} type="button" onClick={() => { setPurpose(p); if (p === "Personal") setUse("private"); else if (workUse) setUse("work"); }}
                    className={`rounded-full border px-3 py-1.5 text-sm ${purpose === p ? "border-primary bg-primary/10 text-primary font-semibold" : "border-line text-ink"}`}>{p}</button>
                ))}
              </div>
              <input value={purpose} onChange={(ev) => setPurpose(ev.target.value)} placeholder="Or type the reason" required className="mt-2 w-full rounded-md border border-line bg-bg px-3 py-2 text-ink" />
            </div>
            <label className="block space-y-1">
              <span className="text-sm font-medium text-ink-muted">Where to <span className="font-normal">(optional)</span></span>
              <input value={to} onChange={(ev) => setTo(ev.target.value)} placeholder="Caboolture exchange" className="w-full rounded-md border border-line bg-bg px-3 py-2 text-ink" />
            </label>
            {use === "work" && (
              <label className="block space-y-1">
                <span className="text-sm font-medium text-ink-muted">Job or site ref <span className="font-normal">(optional)</span></span>
                <input value={jobRef} onChange={(ev) => setJobRef(ev.target.value)} className="w-full rounded-md border border-line bg-bg px-3 py-2 text-ink" />
              </label>
            )}
            {workUse && (
              <div className="grid grid-cols-2 gap-2">
                {(["work", "private"] as const).map((u) => (
                  <button key={u} type="button" onClick={() => setUse(u)} className={`rounded-md border px-3 py-2 text-sm font-medium capitalize ${use === u ? "border-primary bg-primary/10 text-primary" : "border-line"}`}>{u}</button>
                ))}
              </div>
            )}
            {use === "work" && employers.length > 1 && (
              <select value={employer} onChange={(ev) => setEmployer(ev.target.value)} className="w-full rounded-md border border-line bg-bg px-3 py-2 text-ink">
                <option value="">Which employer?</option>
                {employers.map((em) => <option key={em.id} value={em.id}>{em.name}</option>)}
              </select>
            )}
            <ErrorText>{save.error ? "Couldn't save the trip. Try again." : null}</ErrorText>
            <Button type="submit" disabled={save.isPending || distance === null || bad || !purpose.trim()}>{save.isPending ? "Saving…" : "Save trip"}</Button>
            <Button type="button" variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
          </Card>
        </div>
      </form>
    </>
  );
}
